import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import useRazorpay from '../lib/useRazorpay';
import authUtils from '../utils/auth';
import {
  Rocket, ShieldCheck, Loader2, CheckCircle2, Lock, MessageCircle,
  CreditCard, Smartphone, Building2, Wallet,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUPPORT_WA = '918965883955';

const ActivationModal = ({ open, onClose, activationFee = 9, defaults, onActivated }) => {
  const { loaded: rzpLoaded } = useRazorpay();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  // Pre-fill from logged-in user or dashboard defaults
  useEffect(() => {
    if (open && defaults) {
      setForm((f) => ({
        name:  f.name  || defaults.name  || '',
        email: f.email || defaults.email || '',
        phone: f.phone || (defaults.phone || '').replace(/\D/g, '').slice(-10),
      }));
    }
  }, [open]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handlePay = async () => {
    setError('');
    setInfo('');
    const name  = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!name || !email || !phone) { setError('Please fill your name, email and phone'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setError('Please enter a valid email'); return; }
    if (phone.replace(/\D/g, '').length < 10) { setError('Please enter a valid 10-digit phone'); return; }

    setLoading(true);
    try {
      // Step 1 — create Razorpay order
      const { data } = await axios.post(`${API}/razorpay/activation-create-order`, { name, email, phone });

      if (data?.alreadyActivated) {
        setInfo('This email is already activated. Please login from the Login button.');
        setLoading(false);
        return;
      }

      if (!data?.order_id) {
        setError('Could not start payment. Please try again.');
        setLoading(false);
        return;
      }

      if (!rzpLoaded || !window.Razorpay) {
        setError('Payment gateway is loading. Please try again in a moment.');
        setLoading(false);
        return;
      }

      // Step 2 — open Razorpay modal
      const options = {
        key:         data.key,
        amount:      data.amount_paise,
        currency:    'INR',
        name:        'DropSquadz',
        description: `One-Time Activation ₹${activationFee}`,
        image:       '/dropsquadz-logo.jpeg',
        order_id:    data.order_id,
        prefill: {
          name:    name,
          email:   email,
          contact: phone,
        },
        theme: { color: '#2563EB' },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setError('Payment cancelled. Try again when ready.');
          },
        },
        handler: async (response) => {
          // Step 3 — verify payment and activate
          try {
            const { data: verifyData } = await axios.post(`${API}/razorpay/activation-verify`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_signature:  response.razorpay_signature,
            });

            if (verifyData?.activated) {
              // Auto-login the newly activated user
              if (verifyData.access_token && verifyData.user) {
                authUtils.setToken(verifyData.access_token);
                authUtils.setUser(verifyData.user);
              }
              // Notify parent (e.g., navigate to dashboard)
              onActivated?.(verifyData);
              onClose?.();
            } else {
              setError('Activation could not be confirmed. Contact support if the amount was deducted.');
              setLoading(false);
            }
          } catch (vErr) {
            setError(vErr?.response?.data?.detail || 'Payment verified but activation failed. Please contact support.');
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setError(resp?.error?.description || 'Payment failed. Please try again.');
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-md p-0 overflow-hidden border-0 bg-white max-h-[92dvh] overflow-y-auto rounded-2xl">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-600 to-cyan-500 p-5 sm:p-6 text-white">
          <div className="flex items-center gap-2 text-blue-100 text-sm font-medium">
            <Rocket className="w-4 h-4" /> One-Time Activation
          </div>
          <div className="mt-2 flex items-end gap-2">
            <span className="text-4xl font-extrabold">₹{activationFee}</span>
            <span className="line-through text-blue-200 mb-1">₹499</span>
            <span className="ml-auto bg-white/20 rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap">98% OFF Today</span>
          </div>
          <p className="mt-2 text-blue-50 text-sm">
            Buy your one-time activation key &amp; get instant access to your DropSquadz dashboard.
          </p>
        </div>

        <div className="p-5 sm:p-6 space-y-3">
          {/* Bullet benefits */}
          <div className="space-y-2 text-sm">
            {[
              'Get instant dashboard access',
              'We build your Shopify store',
              'Winning product + Meta ads setup',
              'Personal WhatsApp support',
            ].map((t) => (
              <div key={t} className="flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /> {t}
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="space-y-3 pt-2">
            <input
              value={form.name} onChange={update('name')}
              placeholder="Full Name"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-slate-800"
              data-testid="activation-name-input"
            />
            <input
              value={form.email} onChange={update('email')} type="email" inputMode="email" autoComplete="email"
              placeholder="Email Address"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-slate-800"
              data-testid="activation-email-input"
            />
            <input
              value={form.phone} onChange={update('phone')} type="tel" inputMode="numeric" autoComplete="tel" maxLength={13}
              placeholder="WhatsApp Number (10 digits)"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-slate-800"
              data-testid="activation-phone-input"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 space-y-2" data-testid="activation-error">
              <p>{error}</p>
              <a
                href={`https://api.whatsapp.com/send/?phone=${SUPPORT_WA}&text=${encodeURIComponent(`Hi DropSquadz, I want to activate my account (₹${activationFee}). Name: ${form.name}, Email: ${form.email}, Phone: ${form.phone}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-green-700 hover:underline"
              >
                <MessageCircle className="w-4 h-4" /> Get help on WhatsApp
              </a>
            </div>
          )}
          {info && <p className="text-sm text-blue-700">{info}</p>}

          {/* Pay button */}
          <Button
            onClick={handlePay} disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-semibold rounded-xl"
            data-testid="activation-pay-btn"
          >
            {loading
              ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Starting secure checkout…</>)
              : (<>Pay ₹{activationFee} &amp; Activate</>)
            }
          </Button>

          {/* Payment methods */}
          <div className="grid grid-cols-4 gap-2 pt-1">
            {[
              { icon: Smartphone, label: 'UPI' },
              { icon: CreditCard, label: 'Cards' },
              { icon: Building2,  label: 'NetBanking' },
              { icon: Wallet,     label: 'Wallets' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-1 bg-slate-50 border border-slate-100 rounded-lg py-2">
                <Icon className="w-4 h-4 text-slate-500" />
                <span className="text-[10px] text-slate-500">{label}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500 pt-1">
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Secure Razorpay Payment</span>
            <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> 100% Safe</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivationModal;
